import React from 'react';
import './BreedingReport.css';

interface AttrRange {
  min: number;
  max: number;
  likely: number;
  motherVal: number;
  fatherVal: number;
}

interface ParentInfo {
  id: string;
  name: string;
  position: string;
  overall: number;
  gender: string;
  avatarUrl?: string;
}

interface PedigreeNode {
  id: string;
  name: string;
  overall: number;
  gender: string;
  generation: number;
  mother?: PedigreeNode | null;
  father?: PedigreeNode | null;
}

interface BreedingReportProps {
  report: {
    mother: ParentInfo;
    father: ParentInfo;
    attributes: Record<string, AttrRange>;
    projectedOVR: { hitter: number; pitcher: number };
    positionOdds: Record<string, number>;
    genderOdds: Record<string, number>;
    throwingOdds: Record<string, number>;
    battingOdds: Record<string, number>;
    pedigree: { mother: PedigreeNode; father: PedigreeNode };
    inbredAncestorIds: string[];
    cost: number;
  };
  onBreed: () => void;
  onBack: () => void;
  breeding?: boolean;
  balance?: number;
}

const RARITY_COLORS: Record<string, string> = {
  common: '#8b949e',
  uncommon: '#3fb950',
  rare: '#58a6ff',
  epic: '#bc8cff',
};

const ATTR_LABELS: Record<string, string> = {
  power: 'PWR',
  contact: 'CON',
  speed: 'SPD',
  fielding: 'FLD',
  arm: 'ARM',
  discipline: 'DIS',
  endurance: 'END',
  velocity: 'VEL',
  control: 'CTL',
  movement: 'MOV',
  deception: 'DEC',
};

const HITTER_ATTRS = ['power', 'contact', 'speed', 'fielding', 'arm', 'discipline'];
const PITCHER_ATTRS = ['endurance', 'velocity', 'control', 'movement', 'deception'];

function gradeFromVal(val: number): { letter: string; color: string } {
  if (val >= 85) return { letter: 'SS', color: '#a855f7' };
  if (val >= 75) return { letter: 'S', color: '#8b5cf6' };
  if (val >= 65) return { letter: 'A', color: '#22c55e' };
  if (val >= 55) return { letter: 'B', color: '#f59e0b' };
  if (val >= 45) return { letter: 'C', color: '#3b82f6' };
  return { letter: 'D', color: '#ef4444' };
}

const BreedingReport: React.FC<BreedingReportProps> = ({ report, onBreed, onBack, breeding, balance }) => {
  const isHitterMom = report.mother.position !== 'P';
  const isHitterDad = report.father.position !== 'P';
  const showHitter = isHitterMom || isHitterDad;
  const showPitcher = !isHitterMom || !isHitterDad;

  const hitterAttrs = HITTER_ATTRS.filter(a => report.attributes[a]);
  const pitcherAttrs = PITCHER_ATTRS.filter(a => report.attributes[a]);

  const canAfford = (balance || 0) >= report.cost;

  return (
    <div className="breeding-report">
      {/* Header */}
      <div className="br-header">
        <div className="br-crown">👑</div>
        <h2 className="br-title">Breeding Report</h2>
        <p className="br-subtitle">{report.mother.name} + {report.father.name}</p>
      </div>

      {/* Parent Cards */}
      <div className="br-parents">
        <div className="br-parent-card">
          <span className="br-gender-badge female">♀</span>
          <div className="br-parent-avatar">
            {report.mother.avatarUrl ? (
              <img src={report.mother.avatarUrl} alt={report.mother.name} />
            ) : (
              <div className="br-avatar-placeholder">♀</div>
            )}
          </div>
          <div className="br-parent-info">
            <span className="br-parent-name">{report.mother.name}</span>
            <span className="br-parent-ovr">{report.mother.overall} OVR</span>
            <span className="br-parent-pos">{report.mother.position}</span>
          </div>
        </div>

        <div className="br-heart">❤️</div>

        <div className="br-parent-card">
          <span className="br-gender-badge male">♂</span>
          <div className="br-parent-avatar">
            {report.father.avatarUrl ? (
              <img src={report.father.avatarUrl} alt={report.father.name} />
            ) : (
              <div className="br-avatar-placeholder">♂</div>
            )}
          </div>
          <div className="br-parent-info">
            <span className="br-parent-name">{report.father.name}</span>
            <span className="br-parent-ovr">{report.father.overall} OVR</span>
            <span className="br-parent-pos">{report.father.position}</span>
          </div>
        </div>
      </div>

      {/* Grade Legend */}
      <div className="br-grade-legend">
        {[
          { letter: 'D', color: '#ef4444' },
          { letter: 'C', color: '#3b82f6' },
          { letter: 'B', color: '#f59e0b' },
          { letter: 'A', color: '#22c55e' },
          { letter: 'S', color: '#8b5cf6' },
          { letter: 'SS', color: '#a855f7' },
        ].map(g => (
          <span key={g.letter} className="br-grade-chip" style={{ background: g.color }}>{g.letter}</span>
        ))}
      </div>

      {/* Hitter Attributes */}
      {showHitter && hitterAttrs.length > 0 && (
        <div className="br-section">
          <h3 className="br-section-title">⚔️ Hitter Attributes</h3>
          <div className="br-attr-chart">
            {hitterAttrs.map(attr => {
              const range = report.attributes[attr];
              if (!range) return null;
              const likelyGrade = gradeFromVal(range.likely);
              return (
                <div key={attr} className="br-attr-row">
                  <span className="br-attr-label">{ATTR_LABELS[attr]}</span>
                  <div className="br-attr-bar-container">
                    <div className="br-attr-bar-bg">
                      {/* Range bar */}
                      <div
                        className="br-attr-range"
                        style={{
                          left: `${range.min}%`,
                          width: `${range.max - range.min}%`,
                          background: `linear-gradient(90deg, ${likelyGrade.color}44, ${likelyGrade.color}aa)`,
                        }}
                      />
                      {/* Likely indicator */}
                      <div
                        className="br-attr-likely"
                        style={{ left: `${range.likely}%`, background: likelyGrade.color }}
                      />
                    </div>
                    <span className="br-attr-range-text">{range.min}–{range.max}</span>
                  </div>
                  <span className="br-attr-grade" style={{ color: likelyGrade.color }}>
                    {likelyGrade.letter}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pitcher Attributes */}
      {showPitcher && pitcherAttrs.length > 0 && (
        <div className="br-section">
          <h3 className="br-section-title">🎯 Pitcher Attributes</h3>
          <div className="br-attr-chart">
            {pitcherAttrs.map(attr => {
              const range = report.attributes[attr];
              if (!range) return null;
              const likelyGrade = gradeFromVal(range.likely);
              return (
                <div key={attr} className="br-attr-row">
                  <span className="br-attr-label">{ATTR_LABELS[attr]}</span>
                  <div className="br-attr-bar-container">
                    <div className="br-attr-bar-bg">
                      <div
                        className="br-attr-range"
                        style={{
                          left: `${range.min}%`,
                          width: `${range.max - range.min}%`,
                          background: `linear-gradient(90deg, ${likelyGrade.color}44, ${likelyGrade.color}aa)`,
                        }}
                      />
                      <div
                        className="br-attr-likely"
                        style={{ left: `${range.likely}%`, background: likelyGrade.color }}
                      />
                    </div>
                    <span className="br-attr-range-text">{range.min}–{range.max}</span>
                  </div>
                  <span className="br-attr-grade" style={{ color: likelyGrade.color }}>
                    {likelyGrade.letter}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Projected OVR */}
      <div className="br-section">
        <h3 className="br-section-title">📊 Projected OVR</h3>
        <div className="br-ovr-row">
          {showHitter && (
            <div className="br-ovr-card">
              <span className="br-ovr-label">As Hitter</span>
              <span className="br-ovr-value">{report.projectedOVR.hitter}</span>
              <span className="br-ovr-grade" style={{ color: gradeFromVal(report.projectedOVR.hitter).color }}>
                {gradeFromVal(report.projectedOVR.hitter).letter}
              </span>
            </div>
          )}
          {showPitcher && (
            <div className="br-ovr-card">
              <span className="br-ovr-label">As Pitcher</span>
              <span className="br-ovr-value">{report.projectedOVR.pitcher}</span>
              <span className="br-ovr-grade" style={{ color: gradeFromVal(report.projectedOVR.pitcher).color }}>
                {gradeFromVal(report.projectedOVR.pitcher).letter}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Odds & Tendencies */}
      <div className="br-section">
        <h3 className="br-section-title">🎲 Odds & Tendencies</h3>
        <div className="br-odds-grid">
          {/* Position */}
          <div className="br-odds-item">
            <span className="br-odds-label">Position</span>
            <div className="br-odds-bars">
              {Object.entries(report.positionOdds).map(([pos, pct]) => (
                <div key={pos} className="br-odds-bar-row">
                  <span className="br-odds-key">{pos}</span>
                  <div className="br-odds-bar-bg">
                    <div className="br-odds-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="br-odds-pct">{pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gender */}
          <div className="br-odds-item">
            <span className="br-odds-label">Gender</span>
            <div className="br-odds-bars">
              <div className="br-odds-bar-row">
                <span className="br-odds-key">♂ Male</span>
                <div className="br-odds-bar-bg">
                  <div className="br-odds-bar-fill male" style={{ width: `${report.genderOdds.male}%` }} />
                </div>
                <span className="br-odds-pct">{report.genderOdds.male}%</span>
              </div>
              <div className="br-odds-bar-row">
                <span className="br-odds-key">♀ Female</span>
                <div className="br-odds-bar-bg">
                  <div className="br-odds-bar-fill female" style={{ width: `${report.genderOdds.female}%` }} />
                </div>
                <span className="br-odds-pct">{report.genderOdds.female}%</span>
              </div>
            </div>
          </div>



          {/* Handedness */}
          <div className="br-odds-item">
            <span className="br-odds-label">Throws</span>
            <div className="br-odds-bars">
              {Object.entries(report.throwingOdds).map(([hand, pct]) => (
                <div key={hand} className="br-odds-bar-row">
                  <span className="br-odds-key">{hand}</span>
                  <div className="br-odds-bar-bg">
                    <div className="br-odds-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="br-odds-pct">{pct}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="br-odds-item">
            <span className="br-odds-label">Bats</span>
            <div className="br-odds-bars">
              {Object.entries(report.battingOdds).map(([hand, pct]) => (
                <div key={hand} className="br-odds-bar-row">
                  <span className="br-odds-key">{hand}</span>
                  <div className="br-odds-bar-bg">
                    <div className="br-odds-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="br-odds-pct">{pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pedigree */}
      <div className="br-section">
        <h3 className="br-section-title">🧬 Bloodline</h3>
        <p className="br-pedigree-note">
          Family tree up to 3 generations. Inbreeding is highlighted in red.
        </p>
        <div className="br-pedigree">
          <PedigreeTree
            node={report.pedigree.mother}
            label="♀ Mother"
            inbredIds={new Set(report.inbredAncestorIds)}
          />
          <PedigreeTree
            node={report.pedigree.father}
            label="♂ Father"
            inbredIds={new Set(report.inbredAncestorIds)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="br-actions">
        <button className="btn-back" onClick={onBack} disabled={breeding}>
          ← Back
        </button>
        <button
          className="btn-gold"
          onClick={onBreed}
          disabled={breeding || !canAfford}
        >
          {breeding ? 'Breeding...' : `🥚 Breed — ${report.cost.toLocaleString()} $BALLS`}
        </button>
        {!canAfford && <div className="br-warning">Not enough $BALLS</div>}
      </div>
    </div>
  );
};

// Pedigree tree sub-component
const PedigreeTree: React.FC<{
  node: PedigreeNode;
  label: string;
  inbredIds: Set<string>;
  depth?: number;
}> = ({ node, label, inbredIds, depth = 0 }) => {
  if (!node) return null;
  const isInbred = inbredIds.has(node.id);
  const nodeColor = '#8b949e';

  return (
    <div className={`br-pedigree-node depth-${depth}`}>
      <div className={`br-pedigree-card ${isInbred ? 'inbred' : ''}`}>
        <span className="br-pedigree-name" style={{ borderColor: nodeColor }}>
          {node.name}
        </span>
        <span className="br-pedigree-meta">
          {node.overall} OVR
        </span>
        {depth === 0 && <span className="br-pedigree-label">{label}</span>}
        {isInbred && <span className="br-inbred-badge">⚠ INBRED</span>}
      </div>
      {(node.mother || node.father) && depth < 3 && (
        <div className="br-pedigree-children">
          {node.mother && <PedigreeTree node={node.mother} label="♀" inbredIds={inbredIds} depth={depth + 1} />}
          {node.father && <PedigreeTree node={node.father} label="♂" inbredIds={inbredIds} depth={depth + 1} />}
        </div>
      )}
    </div>
  );
};

export default BreedingReport;
